import React, { useState, useEffect } from "react";
import { Menu, X, Droplets, Globe } from "lucide-react";
import { useLang } from "@/i18n";
import { useWizard } from "@/seo/WizardContext";
import { Button } from "@/components/ui/button";

const Header = () => {
  const { t, lang, toggle } = useLang();
  const { openWizard } = useWizard();
  const onRequest = openWizard;
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "services", label: t.nav.services },
    { id: "pricing", label: t.nav.pricing },
    { id: "about", label: t.nav.about },
    { id: "become-cleaner", label: t.nav.becomeCleaner },
    { id: "contact", label: t.nav.contact },
  ];

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    else window.location.href = `/#${id}`;
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 transition-all ${
        scrolled ? "bg-white/90 backdrop-blur-xl border-b border-slate-100 shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-8">
        <a
          href="/"
          data-testid="logo-link"
          className="flex items-center gap-2 font-heading text-xl font-bold text-slate-900"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600 text-white">
            <Droplets className="h-5 w-5" />
          </span>
          MyCleaner
        </a>

        <nav className="hidden items-center gap-8 lg:flex">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm font-medium text-slate-600 transition-colors hover:text-blue-600"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-blue-600 hover:text-blue-600"
          >
            <Globe className="h-4 w-4" />
            {lang.toUpperCase()}
          </button>
          <Button
            data-testid="header-cta-btn"
            onClick={onRequest}
            className="hidden rounded-full bg-blue-600 px-6 font-semibold text-white hover:bg-blue-700 sm:inline-flex"
          >
            {t.nav.cta}
          </Button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="rounded-lg p-2 text-slate-700 lg:hidden"
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="border-t border-slate-100 bg-white px-5 py-4 lg:hidden">
          <div className="flex flex-col gap-1">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-${l.id}`}
                onClick={() => go(l.id)}
                className="rounded-lg px-3 py-3 text-left text-base font-medium text-slate-700 hover:bg-slate-50"
              >
                {l.label}
              </button>
            ))}
            <Button
              data-testid="mobile-header-cta-btn"
              onClick={() => {
                setOpen(false);
                onRequest();
              }}
              className="mt-2 w-full rounded-full bg-blue-600 py-6 font-semibold text-white hover:bg-blue-700"
            >
              {t.nav.cta}
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
